/**
 * Time Entries API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  TimeEntry,
  TimeEntryCreateData,
  TimeEntryUpdateData,
  TimeEntryFilters,
  TimerStartData,
  Timesheet,
  TimeSuggestion,
  SuggestionApproval,
  BulkApprovalResult,
  DailyReviewData,
  WeeklySummary,
} from '@/types'

export const timeEntriesApi = {
  /**
   * List time entries with filtering and pagination
   */
  async list(params?: PaginationParams & TimeEntryFilters): Promise<PaginatedResponse<TimeEntry>> {
    const response = await apiClient.get<PaginatedResponse<TimeEntry>>('/time-entries', { params })
    return response.data
  },

  /**
   * Get time entry by ID
   */
  async get(id: string): Promise<TimeEntry> {
    const response = await apiClient.get<ApiResponse<TimeEntry>>(`/time-entries/${id}`)
    return response.data.data
  },

  /**
   * Create a new time entry
   */
  async create(data: TimeEntryCreateData): Promise<TimeEntry> {
    const response = await apiClient.post<ApiResponse<TimeEntry>>('/time-entries', data)
    return response.data.data
  },

  /**
   * Update a time entry
   */
  async update(id: string, data: TimeEntryUpdateData): Promise<TimeEntry> {
    const response = await apiClient.put<ApiResponse<TimeEntry>>(`/time-entries/${id}`, data)
    return response.data.data
  },

  /**
   * Delete a time entry
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/time-entries/${id}`)
  },

  /**
   * Start a timer
   */
  async startTimer(data: TimerStartData): Promise<TimeEntry> {
    const response = await apiClient.post<ApiResponse<TimeEntry>>('/time-entries/start-timer', data)
    return response.data.data
  },

  /**
   * Stop the running timer
   */
  async stopTimer(): Promise<{ data: TimeEntry; elapsed_minutes: number }> {
    const response = await apiClient.post<{ data: TimeEntry; elapsed_minutes: number }>('/time-entries/stop-timer')
    return response.data
  },

  /**
   * Get currently running timer
   */
  async getRunningTimer(): Promise<TimeEntry | null> {
    const response = await apiClient.get<ApiResponse<TimeEntry | null>>('/time-entries/running')
    return response.data.data
  },

  /**
   * Get timesheet for a week
   */
  async getTimesheet(startDate?: string): Promise<Timesheet> {
    const response = await apiClient.get<ApiResponse<Timesheet>>('/time-entries/timesheet', {
      params: { start_date: startDate },
    })
    return response.data.data
  },

  // ===========================================================================
  // AI Time Suggestions
  // ===========================================================================

  /**
   * Get AI-powered time suggestions for a date
   */
  async getSuggestions(date?: string): Promise<TimeSuggestion[]> {
    const response = await apiClient.get<{ data: TimeSuggestion[]; date: string }>('/time-entries/suggestions', {
      params: { date },
    })
    return response.data.data
  },

  /**
   * Bulk approve/reject time suggestions
   */
  async approveSuggestions(approvals: SuggestionApproval[]): Promise<BulkApprovalResult> {
    const response = await apiClient.post<ApiResponse<BulkApprovalResult>>('/time-entries/suggestions/approve', {
      approvals,
    })
    return response.data.data
  },

  /**
   * Get complete daily review data
   */
  async getDailyReview(date?: string): Promise<DailyReviewData> {
    const response = await apiClient.get<ApiResponse<DailyReviewData>>('/time-entries/suggestions/daily-review', {
      params: { date },
    })
    return response.data.data
  },

  /**
   * Get AI-powered weekly summary
   */
  async getWeeklySummary(weekStart?: string): Promise<WeeklySummary> {
    const response = await apiClient.get<ApiResponse<WeeklySummary>>('/time-entries/weekly-summary', {
      params: { week_start: weekStart },
    })
    return response.data.data
  },
}
